import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { UserForm } from "./UserForm";
import { useClientRoleOptions, useRoleOptions } from "@/hooks/useEnumOptions";
import {
  CreateUserSchema,
  CreateUserClientSchema,
  CreateUserFormData,
  DEFAULT_CREATE_USER,
} from "@/types/forms/shared/user.form";

const FORM_ID = "create-user-form";

type UserCreateModalProps = {
  open: boolean;
  onClose: () => void;
  createUserMutation: {
    mutateAsync: (data: CreateUserFormData) => Promise<unknown>;
    isPending: boolean;
  };
  isBackOffice?: boolean;
};

export const UserCreateModal = ({
  open,
  onClose,
  createUserMutation,
  isBackOffice = false,
}: UserCreateModalProps) => {
  const { t } = useTranslation("shared");
  const { mutateAsync: createUser, isPending } = createUserMutation;

  const schema = isBackOffice ? CreateUserSchema : CreateUserClientSchema;
  const roleOptions = useRoleOptions();
  const clientRoleOptions = useClientRoleOptions();
  const rolesOptions = isBackOffice ? roleOptions : clientRoleOptions;

  const { control, handleSubmit, reset } = useForm<CreateUserFormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_CREATE_USER,
  });

  useEffect(() => {
    if (open) reset(DEFAULT_CREATE_USER);
  }, [open, reset]);

  const onSubmit: SubmitHandler<CreateUserFormData> = async (data) => {
    await createUser(data);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!isPending}
      title={t("userCreateModal.title")}
      footer={
        <Button type="submit" form={FORM_ID} disabled={isPending} className="w-full">
          {isPending ? <Loader2 className="animate-spin" /> : t("userCreateModal.submit")}
        </Button>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
        <UserForm control={control} rolesOptions={rolesOptions} showPassword />
      </form>
    </Modal>
  );
};
